// components/auth/auth-success.tsx
'use client';

import { Button } from '@ahafood/ui/components/button';
import { CheckCircle, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';

interface AuthSuccessProps {
  phoneNumber: string;
  onContinue: () => void;
}

export function AuthSuccess({ phoneNumber, onContinue }: AuthSuccessProps) {
  const [countdown, setCountdown] = useState(3);

  // Decrement countdown every second and clear interval at 0
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Trigger onContinue once countdown reaches zero
  useEffect(() => {
    if (countdown === 0) {
      onContinue();
    }
  }, [countdown, onContinue]);

  const formatPhoneDisplay = (phone: string): string => {
    if (phone.startsWith('84')) {
      const withoutPrefix = '0' + phone.slice(2);
      return withoutPrefix.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
    }
    return phone.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  };

  return (
    <div className="text-center space-y-6 py-4">
      {/* Success Icon with Animation */}
      <div className="relative mx-auto">
        <div className="size-20 bg-gradient-to-br from-green-50 to-green-60 rounded-full flex items-center justify-center mx-auto relative overflow-hidden">
          <CheckCircle className="size-10 text-white z-10" />

          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-green-40 to-green-50 animate-pulse rounded-full" />
        </div>

        {/* Sparkles animation */}
        <div className="absolute -top-2 -right-2">
          <Sparkles className="size-6 text-yellow-50 animate-bounce" />
        </div>
        <div className="absolute -bottom-2 -left-2">
          <Sparkles className="size-4 text-yellow-40 animate-bounce delay-300" />
        </div>
      </div>

      {/* Success Message */}
      <div className="space-y-3">
        <h2 className="text-2xl font-bold text-green-70">
          Đăng nhập thành công! 🎉
        </h2>
        <p className="text-neutral-60 text-sm">
          Tài khoản{' '}
          <span className="font-semibold text-secondary-60">
            {formatPhoneDisplay(phoneNumber)}
          </span>{' '}
          đã được xác thực thành công
        </p>
      </div>

      {/* Benefits */}
      {/* <div className="bg-gradient-to-r from-primary-10 to-yellow-10 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-center gap-2 text-primary-60">
          <Gift className="size-5" />
          <h3 className="font-semibold">Ưu đãi dành cho bạn</h3>
        </div>

        <div className="text-sm text-neutral-60 space-y-1">
          <p>✅ Gợi ý món ăn cá nhân hóa</p>
          <p>✅ Ưu đãi độc quyền từ AI</p>
          <p>✅ Đặt hàng nhanh chóng</p>
        </div>
      </div> */}

      {/* Auto redirect countdown */}
      <div className="space-y-4">
        <p className="text-sm text-neutral-50">
          Tự động chuyển hướng sau {countdown}s
        </p>

        <Button
          onClick={onContinue}
          className="w-full h-12 text-lg bg-gradient-to-r from-green-50 to-green-60 hover:from-green-60 hover:to-green-70"
        >
          Tiếp tục khám phá ({countdown})
        </Button>
      </div>
    </div>
  );
}
